import { InventoryHoldArea, SHELVING_DEFAULT_FORM_VALUES } from "../../../constants"
import type { InventoryDestinationFormValues } from "../../../schemas"
import { formatInventoryLocation } from "../../../utils"
import { HOLD_AREA_TO_DESTINATION_AREA } from "../constants"
import type {
  InterTransferPalletFormValues,
  InterTransferPalletSelectionFormValues,
  IntraTransferPalletSelectionFormValues,
  TransferFormValues,
  TransferLpnFormValues,
} from "../schemas/transfer"
import type {
  TransferFormPallet,
  TransferFormProductInventory,
  TransferPalletWithProduct,
  TransferTotals,
} from "../types"

import {
  type CreateTransferInput,
  type InventoryPalletGroup,
  type InventoryPalletLot,
  InventoryStatus,
  type Product,
  type Transfer,
  TransferAs400SyncStatus,
  TransferLotSyncStatus,
  type TransferPallet,
  type TransferProduct,
  type TransferProductInput,
  TransferStatus,
  TransferType,
  type UpdateTransferInput,
  type UpdateTransferPalletLpnsInput,
  type WarehousePalletDestinationInput,
} from "@/graphql/codegen/graphql"
import { toStringOrNull } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"
import type { PalletLabelParams } from "@/utils/printing/generatePalletLabelZpl"
import { isNil } from "@/utils/utils"

export const getLotRowBorderClassName = (isLastLot: boolean): string | undefined =>
  isLastLot ? undefined : "[&>td]:border-b-0"

export const calculateProductQtyToMove = ({ pallets }: TransferFormProductInventory): number =>
  pallets.reduce((sum, pallet) => sum + pallet.lots.reduce((lotSum, lot) => lotSum + lot.qtyToMove, 0), 0)

export const toInterTransferPalletFormValues = (
  { lots: palletLots, ...pallet }: InventoryPalletGroup & Pick<TransferFormPallet, "lpn">,
  lots: (InventoryPalletLot & { qtyToMove?: number | null })[] = palletLots
): InterTransferPalletFormValues => ({
  ...pallet,
  lpn: pallet.lpn ?? "",
  lots: lots.map((lot) => ({ ...lot, qtyToMove: lot.qtyToMove ?? null })),
})

export const toInterTransferFormPallets = ({ pallets }: InterTransferPalletSelectionFormValues): TransferFormPallet[] =>
  pallets.map(({ lots, ...pallet }) => ({
    ...pallet,
    lots: lots.map((lot) => ({ ...lot, qtyToMove: lot.qtyToMove! })),
  }))

export const toIntraTransferFormPallets = ({ pallets }: IntraTransferPalletSelectionFormValues): TransferFormPallet[] =>
  pallets.map((pallet) => ({
    ...pallet,
    lots: pallet.lots.map((lot) => ({ ...lot, qtyToMove: lot.totalUnits ?? 0 })),
  }))

// Saved transfer lots don't carry all raw inventory fields, so the rest comes from the reference pallet groups.
export const getPalletsWithMissingLotDetailsFilled = <
  TPallet extends { referenceNbr: number; lots: { rawInventoryId: string }[] },
>(
  pallets: TPallet[],
  palletGroups: InventoryPalletGroup[]
): TPallet[] => {
  const inventoryLotByReferenceNbr = new Map(
    palletGroups.map(({ referenceNbr, lots }) => [referenceNbr, new Map(lots.map((lot) => [lot.rawInventoryId, lot]))])
  )

  return pallets.map((pallet) => {
    const inventoryLot = inventoryLotByReferenceNbr.get(pallet.referenceNbr)

    return inventoryLot
      ? { ...pallet, lots: pallet.lots.map((lot) => ({ ...lot, ...inventoryLot.get(lot.rawInventoryId) })) }
      : pallet
  })
}

const formatDestinationShelving = (
  shelving: { aisleCode?: string | null; sectionCode?: string | null; binNumber?: number | null } | null | undefined
): string => {
  if (!shelving) return ""

  const { aisleCode, sectionCode, binNumber } = shelving
  if (!aisleCode || !sectionCode || isNil(binNumber)) return ""

  return `${aisleCode} / ${sectionCode} / ${binNumber}`
}

export const formatPalletFormDestination = (destination: InventoryDestinationFormValues | undefined): string => {
  if (!destination) return ""

  return destination.holdArea !== InventoryHoldArea.Warehouse
    ? formatInventoryLocation(destination.holdArea)
    : formatDestinationShelving(destination.shelving)
}

export const formatPalletDestination = (pallet: TransferPallet | null | undefined): string =>
  pallet?.destinationWarehouseShelving
    ? formatDestinationShelving(pallet.destinationWarehouseShelving)
    : formatInventoryLocation(pallet?.destinationLocation)

type GetSelectedShelvingIdsParams =
  | { inventories: TransferFormProductInventory[]; productId: number }
  | { inventories: TransferFormProductInventory[]; palletReferenceNumber: number }

export const getSelectedShelvingIds = (params: GetSelectedShelvingIdsParams): string[] =>
  params.inventories
    .flatMap((inventory) => inventory.pallets.map((pallet) => ({ ...pallet, productId: inventory.product.productId })))
    .filter((pallet) =>
      // With a pallet given, exclude just it (siblings of the same product stay in the result); otherwise exclude
      // the whole product, since that caller tracks its own product's pallets separately via live form state.
      "palletReferenceNumber" in params
        ? pallet.referenceNbr !== params.palletReferenceNumber
        : pallet.productId !== params.productId
    )
    .map((pallet) => pallet.destination?.shelving.warehouseShelvingId)
    .filter((warehouseShelvingId) => !isNil(warehouseShelvingId))

const getTotalsUomName = (items: { product: Product }[]): string | null => {
  if (!items.length) return null

  const firstUom = items[0].product.packageUsWeightUom
  const hasSameUom = !isNil(firstUom) && items.every(({ product }) => product.packageUsWeightUom === firstUom)

  return hasSameUom ? (items[0].product.packageUsWeightUomDetails?.name ?? null) : null
}

export const calculateTransferFormTotals = (inventories: TransferFormProductInventory[]): TransferTotals => ({
  ...inventories.reduce(
    (totals, inventory) => ({
      palletCount: totals.palletCount + inventory.pallets.length,
      quantity: totals.quantity + calculateProductQtyToMove(inventory),
    }),
    { palletCount: 0, quantity: 0 }
  ),
  uomName: getTotalsUomName(inventories),
})

export const calculateTransferTotals = (transferProducts: TransferProduct[]): TransferTotals => ({
  ...transferProducts.reduce(
    (totals, transferProduct) => ({
      palletCount: totals.palletCount + transferProduct.pallets.length,
      quantity:
        totals.quantity +
        transferProduct.pallets.reduce(
          (palletSum, pallet) => palletSum + pallet.lots.reduce((lotSum, lot) => lotSum + (lot.qtyToMove ?? 0), 0),
          0
        ),
    }),
    { palletCount: 0, quantity: 0 }
  ),
  uomName: getTotalsUomName(transferProducts),
})

const toLotDestinationInput = (destination?: InventoryDestinationFormValues): WarehousePalletDestinationInput => {
  if (!destination) return {}

  return destination.holdArea === InventoryHoldArea.Warehouse
    ? { destinationWarehouseShelvingId: destination.shelving.warehouseShelvingId }
    : { destinationAreaCode: HOLD_AREA_TO_DESTINATION_AREA[destination.holdArea] }
}

export const toTransferProductInputs = (
  inventories: TransferFormProductInventory[],
  transferType: TransferType
): TransferProductInput[] =>
  inventories.map((inventory) => ({
    productId: inventory.product.productId,
    pallets: inventory.pallets.map((pallet) =>
      transferType === TransferType.InterWarehouse
        ? {
            // Only inter-warehouse transfer pallets have an LPN.
            lpn: toStringOrNull(pallet.lpn),
            // Inter-warehouse transfers can move a partial quantity per lot.
            lots: pallet.lots.map((lot) => ({ rawInventoryId: lot.rawInventoryId, qtyToMove: lot.qtyToMove })),
          }
        : {
            // Only intra-warehouse transfer pallets have a destination.
            destination: toLotDestinationInput(pallet.destination),
            // Intra-warehouse transfers always move whole lots.
            lots: pallet.lots.map((lot) => ({ rawInventoryId: lot.rawInventoryId })),
          }
    ),
  }))

export const toTransferCreateInput = (
  formValues: TransferFormValues,
  inventories: TransferFormProductInventory[],
  isDraft: boolean
): CreateTransferInput => ({
  products: toTransferProductInputs(inventories, formValues.type),
  reason: formValues.reason,
  sourceWarehouseAreaId: formValues.sourceWarehouseAreaId,
  targetWarehouseAreaId: formValues.targetWarehouseAreaId,
  isDraft,
  transferType: formValues.type,
})

export const toTransferUpdateInput = (
  formValues: TransferFormValues,
  inventories: TransferFormProductInventory[],
  transferId: string,
  isDraft: boolean
): UpdateTransferInput => ({
  products: toTransferProductInputs(inventories, formValues.type),
  reason: formValues.reason,
  isDraft,
  transferId,
})

export const toTransferFormValues = (transfer: Transfer): TransferFormValues => ({
  type: transfer.transferType,
  sourceWarehouseAreaId: transfer.sourceWarehouseAreaId,
  targetWarehouseAreaId:
    transfer.transferType === TransferType.InterWarehouse ? transfer.targetWarehouseAreaId : undefined,
  reason: transfer.reason ?? undefined,
  comment: "",
  documents: [],
})

export const isTransferAdjustmentUpsertFailed = (transfer: Transfer): boolean =>
  transfer.as400SyncStatus === TransferAs400SyncStatus.AdjustmentUpsertFailed

// AK-6098 AC4: An inter-warehouse transfer has no dedicated backend status for a failed sync, so it is partially failed
// when some lots synced and failed creation when every lot failed.
export const isTransferPartiallyFailed = (transfer: Transfer): boolean =>
  isTransferAdjustmentUpsertFailed(transfer) &&
  transfer.products
    .flatMap(({ pallets }) => pallets.flatMap(({ lots }) => lots))
    .some(({ as400SyncStatus }) => as400SyncStatus !== TransferLotSyncStatus.Failed)

// A transfer (and its documents) can only be modified while `Draft`, `PendingPickAndPutaway`, or an
// inter-warehouse transfer in `PendingPick` whose adjustment creation failed — matches the backend's updateTransfer
// guard. `transfer` is undefined for a not-yet-created transfer, which is always editable.
export const isTransferEditable = (transfer?: Transfer): boolean =>
  !transfer ||
  [TransferStatus.Draft, TransferStatus.PendingPickAndPutaway].includes(transfer.status) ||
  (transfer.status === TransferStatus.PendingPick && isTransferAdjustmentUpsertFailed(transfer))

// AK-5563 AC5: `PendingPickAndPutaway` is set before AS400 responds, so status alone doesn't mean the work order exists.
export const isTransferWorkOrderUpserted = (transfer: Transfer): boolean =>
  transfer.transferType === TransferType.IntraWarehouse &&
  transfer.status !== TransferStatus.Draft &&
  !(
    transfer.as400SyncStatus &&
    [TransferAs400SyncStatus.WorkOrderUpserting, TransferAs400SyncStatus.WorkOrderUpsertFailed].includes(
      transfer.as400SyncStatus
    )
  )

// AK-6098: `PendingPick` is set before AS400 responds, so status alone doesn't mean the adjustments exist.
export const isTransferAdjustmentUpserted = (transfer: Transfer): boolean =>
  transfer.transferType === TransferType.InterWarehouse &&
  transfer.status !== TransferStatus.Draft &&
  !(
    transfer.as400SyncStatus &&
    [TransferAs400SyncStatus.AdjustmentUpserting, TransferAs400SyncStatus.AdjustmentUpsertFailed].includes(
      transfer.as400SyncStatus
    )
  )

export const toTransferLpnFormValues = (transfer: Transfer): TransferLpnFormValues => ({
  pallets: transfer.products.flatMap(({ pallets }) =>
    pallets.map((pallet) => ({
      transferPalletId: pallet.transferPalletId,
      referenceNbr: pallet.referenceNbr,
      lpn: pallet.lpn ?? "",
    }))
  ),
})

export const toTransferLpnUpdateInput = (
  { pallets }: TransferLpnFormValues,
  transferId: string
): UpdateTransferPalletLpnsInput => ({
  transferId,
  pallets: pallets.map(({ transferPalletId, lpn }) => ({ transferPalletId, lpn: toStringOrNull(lpn) })),
})

const toDestinationFormValues = (pallet: TransferPallet): InventoryDestinationFormValues | undefined => {
  if (!pallet.destinationWarehouseShelvingId && !pallet.destinationLocation) return undefined

  return pallet.destinationWarehouseShelvingId
    ? {
        holdArea: InventoryHoldArea.Warehouse,
        shelving: {
          warehouseShelvingId: pallet.destinationWarehouseShelvingId,
          aisleCode: pallet.destinationWarehouseShelving?.aisleCode ?? null,
          sectionCode: pallet.destinationWarehouseShelving?.sectionCode ?? null,
          binNumber: pallet.destinationWarehouseShelving?.binNumber ?? null,
          binSize: pallet.destinationWarehouseShelving?.binSize ?? null,
        },
      }
    : {
        holdArea: pallet.destinationLocation as InventoryHoldArea,
        shelving: SHELVING_DEFAULT_FORM_VALUES,
      }
}

const toTransferFormPallets = (transferProduct: TransferProduct): TransferFormPallet[] =>
  transferProduct.pallets.map((pallet) => ({
    referenceNbr: pallet.referenceNbr,
    lpn: pallet.lpn,
    location: pallet.lots[0]?.rawInventory?.location ?? "",
    totalQoh: pallet.lots.reduce((sum, lot) => sum + (lot.rawInventory?.totalUnits ?? 0), 0),
    lots: pallet.lots.map((lot) => ({
      rawInventoryId: lot.rawInventoryId!,
      lotNbr: lot.rawInventory?.lotNbr,
      batchNbr: lot.rawInventory?.batchNbr,
      totalUnits: lot.rawInventory?.totalUnits,
      totalPackages: lot.rawInventory?.totalPackages,
      uom: lot.rawInventory?.uom,
      isLocked: false,
      isLockedByOpenGr: false,
      hasProductTest: true,
      qtyToMove: lot.qtyToMove ?? lot.rawInventory?.totalUnits ?? 0,
    })),
    destination: toDestinationFormValues(pallet),
  }))

export const toTransferFormProductInventories = (transfer: Transfer): TransferFormProductInventory[] =>
  transfer.products.map((transferProduct) => ({
    product: transferProduct.product,
    qtyOnHand: transferProduct.qtyOnHand,
    availableQty: 0,
    balToCome: 0,
    hardAllocatedQty: 0,
    inventoryStatus: InventoryStatus.Good,
    moh: 0,
    mohInStock: 0,
    onHoldQty: 0,
    softAllocatedQty: 0,
    warehouses: [],
    pallets: toTransferFormPallets(transferProduct),
  }))

export const toTransferPalletLabelParams = ({
  product,
  referenceNbr,
  lots,
}: TransferPalletWithProduct): PalletLabelParams => {
  const countByPackageQuantity = new Map<number, number>()
  let mostCommonPackageQuantity: number | null = null
  let highestPackageQuantityCount = 0
  let palletQuantity = 0
  let packageCount = 0

  for (const { rawInventory } of lots) {
    const totalUnits = rawInventory?.totalUnits ?? 0
    const totalPackages = rawInventory?.totalPackages ?? 0

    palletQuantity += totalUnits

    if (!totalPackages) continue

    packageCount += totalPackages

    // Round before grouping, so floating-point noise doesn't split a shared package size into separate keys.
    const packageQuantity = roundToPrecision(totalUnits / totalPackages, 2)
    const count = (countByPackageQuantity.get(packageQuantity) ?? 0) + 1
    countByPackageQuantity.set(packageQuantity, count)

    if (count > highestPackageQuantityCount) {
      highestPackageQuantityCount = count
      mostCommonPackageQuantity = packageQuantity
    }
  }

  return {
    productName: product.companyProductDescription ?? "Unknown Product",
    referenceId: String(referenceNbr),
    stockNumber: product.publicId ?? "N/A",
    palletQuantity: String(roundToPrecision(palletQuantity, 2)),
    packageQuantity: String(mostCommonPackageQuantity ?? "N/A"),
    packageCount: Math.round(packageCount),
    lots: lots.map(({ rawInventory }) => ({
      lotNumber: rawInventory?.lotNbr,
      batchNumber: rawInventory?.batchNbr,
      quantity: rawInventory?.totalUnits,
    })),
    uomName: product.packageUsWeightUomDetails?.name ?? "N/A",
  }
}
